<template>
  <div class="space-y-8">
    <div>
      <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">
        {{ isEdit ? 'Edit external runner' : 'Add external runner' }}
      </h1>
      <p class="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
        A GitLab or GitHub runner server you run yourself, for example a dedicated Docker host.
        Manage Runners installs only its metrics agent there and never changes the runner.
      </p>
    </div>

    <p v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading runner…</p>
    <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400" role="alert">
      {{ loadError }}
    </p>
    <form v-else class="max-w-xl space-y-6" novalidate @submit.prevent="onSubmit">
      <div>
        <label for="external-name" :class="labelClass">Name</label>
        <input
          id="external-name"
          v-model="form.name"
          type="text"
          :class="inputClass(!!errors.name)"
          placeholder="docker-host-1"
          data-1p-ignore
        />
        <p v-if="errors.name" :class="errorClass">{{ errors.name }}</p>
      </div>

      <fieldset>
        <legend :class="labelClass">Platform</legend>
        <p :class="hintClass">
          Autoscaling registers its burst runners on this platform, so it cannot change while the
          runner has an autoscaling policy.
        </p>
        <div class="mt-2 flex gap-6">
          <label
            v-for="(label, value) in PLATFORM_LABELS"
            :key="value"
            class="flex items-center gap-2 text-sm text-gray-900 dark:text-white"
          >
            <input
              v-model="form.platform"
              type="radio"
              name="external-platform"
              :value="value"
              class="size-4 text-indigo-600"
            />
            {{ label }}
          </label>
        </div>
      </fieldset>

      <div>
        <label for="external-labels" :class="labelClass">Labels</label>
        <LabelsInput id="external-labels" v-model="form.labels" :error="!!errors.labels" />
        <p v-if="errors.labels" :class="errorClass">{{ errors.labels }}</p>
        <p v-else :class="hintClass">Shown on the dashboard, for example the runner's tags.</p>
      </div>

      <fieldset aria-describedby="external-metrics-desc">
        <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">Metrics</legend>
        <p id="external-metrics-desc" :class="hintClass">
          The agent measures CPU, RAM, swap and disk usage and the running jobs every minute.
          Changes here take effect when you run a new install command on the server.
        </p>
        <div class="mt-4 space-y-6">
          <RunnerToggle
            id="external-monitoring"
            v-model="form.monitoringEnabled"
            label="Show in dashboard"
            description="Charts on the runner card."
            :disabled="!canChangeMonitoring && !form.monitoringEnabled"
          />
          <MetricsSelect v-model="form.metricsConnectionId" />
        </div>
      </fieldset>

      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          @click="router.push({ name: 'Dashboard' })"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="submitting || planRequired || !organizations.canManageRunners"
          data-testid="external-submit"
        >
          {{ submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add runner' }}
        </button>
      </div>
      <PlanRequiredNotice v-if="planRequired" :recheck="refreshPlan" />
      <p v-else-if="serverError" class="text-sm text-red-600 dark:text-red-400" role="alert">
        {{ serverError }}
      </p>
    </form>

    <InstallCommandDialog
      v-if="created"
      :open="!!created"
      :runner-id="created.runnerId"
      :runner-name="created.name"
      @close="router.push({ name: 'Dashboard' })"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LabelsInput from '@/components/LabelsInput.vue';
import MetricsSelect from '@/components/MetricsSelect.vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import PlanRequiredNotice from '@/components/PlanRequiredNotice.vue';
import InstallCommandDialog from '@/components/InstallCommandDialog.vue';
import {
  createExternalRunner,
  type ExternalRunnerRequest,
  type ExternalRunnerResponse,
  getExternalRunner,
  updateExternalRunner,
} from '@/api/external-runners';
import { runnerProblem, type RunnerPlatform } from '@/api/runners';
import { PLATFORM_LABELS } from '@/lib/platforms';
import { installable } from '@/lib/external-runners';
import { usePlanRequired } from '@/lib/entitlement';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';
import { isSubscriptionRequired } from '@/lib/subscription-required';
import { useOrganizationStore } from '@/stores/organization';

const route = useRoute();
const router = useRouter();
const organizations = useOrganizationStore();
const { planRequired, refresh: refreshPlan } = usePlanRequired();
const { canChange: canChangeMonitoring } = useMonitoringEntitlement();

const runnerId = computed(() => (route.params.id as string | undefined) ?? '');
const isEdit = computed(() => !!runnerId.value);

const form = reactive({
  name: '',
  platform: 'GITLAB' as RunnerPlatform,
  labels: [] as string[],
  monitoringEnabled: true,
  metricsConnectionId: '',
});
const errors = reactive({ name: '', labels: '' });
const loading = ref(false);
const loadError = ref('');
const submitting = ref(false);
const serverError = ref('');
const created = ref<ExternalRunnerResponse | null>(null);

const labelClass = 'block text-sm/6 font-medium text-gray-900 dark:text-white';
const hintClass = 'mt-1 text-sm text-gray-500 dark:text-gray-400';
const errorClass = 'mt-2 text-sm text-red-600 dark:text-red-400';
function inputClass(invalid: boolean) {
  return [
    'mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 dark:bg-white/5 dark:text-white',
    invalid
      ? 'outline-red-500 focus:outline-red-600'
      : 'outline-gray-300 focus:outline-indigo-600 dark:outline-white/10',
  ];
}

// The API's rules (ExternalRunnerValidation in the backend).
const NAME = /^[a-zA-Z0-9-]+(?: +[a-zA-Z0-9-]+)*$/;
const LABEL = /^[a-zA-Z0-9][a-zA-Z0-9._:/-]{0,63}$/;

function validate(): boolean {
  errors.name = NAME.test(form.name.trim()) ? '' : "Use letters, digits, spaces and '-'.";
  errors.labels =
    form.labels.length <= 32 && form.labels.every((label) => LABEL.test(label))
      ? ''
      : 'Use up to 32 labels of up to 64 letters, digits and . _ : / -, starting with a letter or digit.';
  return !errors.name && !errors.labels;
}

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const runner = await getExternalRunner(runnerId.value);
    form.name = runner.name;
    form.platform = runner.platform;
    form.labels = [...runner.labels];
    form.monitoringEnabled = runner.monitoringEnabled;
    form.metricsConnectionId = runner.metricsConnectionId ?? '';
  } catch (e) {
    loadError.value = runnerProblem(e, 'Failed to load the external runner.').message;
  } finally {
    loading.value = false;
  }
});

async function onSubmit() {
  serverError.value = '';
  if (!validate()) return;
  const request: ExternalRunnerRequest = {
    name: form.name.trim(),
    platform: form.platform,
    labels: form.labels,
    monitoringEnabled: form.monitoringEnabled,
    metricsConnectionId: form.metricsConnectionId,
  };
  submitting.value = true;
  try {
    if (isEdit.value) {
      await updateExternalRunner(runnerId.value, request);
      await router.push({ name: 'Dashboard' });
      return;
    }
    const runner = await createExternalRunner(request);
    if (installable(runner)) created.value = runner;
    else await router.push({ name: 'Dashboard' });
  } catch (e) {
    if (isSubscriptionRequired(e)) void refreshPlan();
    serverError.value = runnerProblem(e, 'Saving the external runner failed.').message;
  } finally {
    submitting.value = false;
  }
}
</script>
